import type { MetricResponse, MetricValue, RecentActivity } from '@interfaces/metric.interface';

import { LOCK_ACTIVITIES } from '../pools';
import { daysFromNow, pick, randomInt } from '../utils';

function buildMetricValue(
  type: string,
  value: number,
  temporality: string,
  valueCalc?: number
): MetricValue {
  return { type, value, valueCalc, temporality };
}

function buildRecentActivity(index: number): RecentActivity {
  return {
    access: String(randomInt(1000, 9999)),
    activity: pick(LOCK_ACTIVITIES),
    date: daysFromNow(-index * randomInt(1, 3)),
    time: `${String(randomInt(6, 23)).padStart(2, '0')}:${String(randomInt(0, 59)).padStart(2, '0')}`,
    status: randomInt(1, 10) <= 8 ? 'SUCCESS' : 'FAILED',
  };
}

export function buildActivityOverview(): MetricResponse {
  return {
    metrics: [
      buildMetricValue('TOTAL_BOOKINGS', randomInt(5, 30), 'LAST_WEEK', randomInt(5, 20)),
      buildMetricValue('CANCELLATION_WEEK', randomInt(0, 5), 'LAST_WEEK', randomInt(0, 10)),
      buildMetricValue('OCCUPANCY_RATE', randomInt(30, 95), 'LAST_MONTH', randomInt(1, 15)),
      buildMetricValue('INCOME_MONTH', randomInt(3000, 20000), 'LAST_MONTH', randomInt(5, 25)),
    ],
  };
}

export function buildAccessMetrics(): MetricResponse {
  return {
    metrics: [
      buildMetricValue('SUCCESSFUL_ACCESS', randomInt(10, 40), 'LAST_WEEK', randomInt(10, 30)),
      buildMetricValue('FAILED_ACCESS', randomInt(0, 10), 'LAST_WEEK', randomInt(0, 15)),
    ],
    recentActivity: Array.from({ length: 4 }, (_, i) => buildRecentActivity(i)),
  };
}

export function buildAccessMetricsOverallView(): MetricResponse {
  return {
    metrics: [
      buildMetricValue('BATTERY', randomInt(60, 100), 'stable', undefined),
      buildMetricValue('SUCCESSFUL_ACCESS', randomInt(10, 40), 'LAST_WEEK', randomInt(10, 30)),
      buildMetricValue('FAILED_ACCESS', randomInt(0, 10), 'LAST_WEEK', randomInt(0, 15)),
    ],
    recentActivity: Array.from({ length: 4 }, (_, i) => buildRecentActivity(i)),
  };
}

export function buildPerformanceMetrics(): MetricResponse {
  return {
    metrics: [
      buildMetricValue('OCCUPANCY_RATE', randomInt(30, 80), 'LAST_MONTH', randomInt(1, 10)),
      buildMetricValue(
        'REVENUE_PER_NIGHT',
        randomInt(150, 500),
        'PREVIOUS_MONTH',
        randomInt(5, 20)
      ),
      buildMetricValue('CANCELLATION_RATE', randomInt(2, 15), 'PREVIOUS_MONTH', randomInt(1, 5)),
      buildMetricValue('TOTAL_REVENUE', randomInt(5000, 25000), 'LAST_YEARS', randomInt(10, 30)),
      buildMetricValue('TOTAL_BOOKINGS', randomInt(15, 60), 'LAST_YEARS', randomInt(5, 15)),
      buildMetricValue(
        'AVERAGE_RATING',
        parseFloat((3.5 + Math.random() * 1.5).toFixed(1)),
        'Excellent'
      ),
    ],
  };
}

export interface BookingStats {
  totalBookingsLastWeek: number;
  bookingsWeekChange: number;
  cancellationsLastWeek: number;
  occupancyRateLastMonth: number;
  occupancyChange: number;
  incomeLastMonth: number;
  incomeChangePercent: number;
  revenuePerNight: number;
  cancellationRatePercent: number;
  totalRevenue: number;
  totalBookings: number;
  averageRating: number;
}

export function buildActivityOverviewFromStats(stats: BookingStats): MetricResponse {
  return {
    metrics: [
      buildMetricValue(
        'TOTAL_BOOKINGS',
        stats.totalBookingsLastWeek,
        'LAST_WEEK',
        stats.bookingsWeekChange
      ),
      buildMetricValue('CANCELLATION_WEEK', stats.cancellationsLastWeek, 'LAST_WEEK', 0),
      buildMetricValue(
        'OCCUPANCY_RATE',
        stats.occupancyRateLastMonth,
        'LAST_MONTH',
        stats.occupancyChange
      ),
      buildMetricValue(
        'INCOME_MONTH',
        stats.incomeLastMonth,
        'LAST_MONTH',
        stats.incomeChangePercent
      ),
    ],
  };
}

export function buildPerformanceMetricsFromStats(stats: BookingStats): MetricResponse {
  return {
    metrics: [
      buildMetricValue(
        'OCCUPANCY_RATE',
        stats.occupancyRateLastMonth,
        'LAST_MONTH',
        stats.occupancyChange
      ),
      buildMetricValue('REVENUE_PER_NIGHT', stats.revenuePerNight, 'PREVIOUS_MONTH', 0),
      buildMetricValue('CANCELLATION_RATE', stats.cancellationRatePercent, 'PREVIOUS_MONTH', 0),
      buildMetricValue('TOTAL_REVENUE', stats.totalRevenue, 'LAST_YEARS', 8),
      buildMetricValue('TOTAL_BOOKINGS', stats.totalBookings, 'LAST_YEARS', 8),
      buildMetricValue('AVERAGE_RATING', stats.averageRating, 'Excellent'),
    ],
  };
}
